// Debug script to diagnose document sending/receiving issues
// Run this in browser console to see what's happening

const debugDocuments = () => {
    console.log('========================================');
    console.log('   DOCUMENT SYSTEM DIAGNOSTIC REPORT   ');
    console.log('========================================\n');

    // 1. Check current user
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('1. CURRENT USER INFO:');
    console.log('   - Email:', currentUser.email);
    console.log('   - ID:', currentUser.id);
    console.log('   - Employee ID:', currentUser.employee_id);
    console.log('   - User ID:', currentUser.user_id);
    console.log('   - Employee Code:', currentUser.employee_code);
    console.log('   - Name:', currentUser.name || `${currentUser.first_name} ${currentUser.last_name}`);
    console.log('   - Role:', currentUser.role || currentUser.role_name);
    console.log('\n');

    // 2. Check all employees in the system
    console.log('2. ALL EMPLOYEES IN SYSTEM:');
    const employeeKeys = ['hrportal_employees', 'employees', 'allEmployees'];
    let foundEmployees = false;

    for (const key of employeeKeys) {
        const employees = JSON.parse(localStorage.getItem(key) || '[]');
        if (employees.length > 0) {
            foundEmployees = true;
            console.log(`   Found ${employees.length} employees in '${key}':`);
            employees.slice(0, 3).forEach(emp => {
                console.log(`   - ${emp.first_name} ${emp.last_name}: ID=${emp.id}, email=${emp.email}, code=${emp.employee_code}`);
            });
            if (employees.length > 3) {
                console.log(`   ... and ${employees.length - 3} more`);
            }
        }
    }

    if (!foundEmployees) {
        console.log('   No employees found in localStorage');
    }
    console.log('\n');

    // 3. Check all document-related localStorage keys
    console.log('3. DOCUMENT STORAGE KEYS:');
    const allKeys = Object.keys(localStorage);

    console.log('   a) Employee Document Keys:');
    const empDocKeys = allKeys.filter(key => key.startsWith('employeeDocuments_'));
    if (empDocKeys.length > 0) {
        empDocKeys.forEach(key => {
            const docs = JSON.parse(localStorage.getItem(key) || '[]');
            const employeeId = key.replace('employeeDocuments_', '');
            console.log(`      ${key}: ${docs.length} documents`);
            if (docs.length > 0) {
                console.log(`         Employee ID: ${employeeId}`);
                console.log(`         Latest doc:`, {
                    category: docs[docs.length - 1].category,
                    templateName: docs[docs.length - 1].templateName,
                    receivedAt: docs[docs.length - 1].receivedAt,
                    type: docs[docs.length - 1].type
                });
            }
        });
    } else {
        console.log('      No employee document keys found');
    }

    console.log('\n   b) Generated Documents:');
    const generatedDocs = JSON.parse(localStorage.getItem('generatedDocuments') || '[]');
    console.log(`      Total generated: ${generatedDocs.length}`);
    if (generatedDocs.length > 0) {
        const sentDocs = generatedDocs.filter(d => d.status === 'sent');
        console.log(`      Sent documents: ${sentDocs.length}`);
        sentDocs.slice(0, 2).forEach(doc => {
            console.log(`         - ${doc.category} sent to ${doc.sentToName} (ID: ${doc.sentTo})`);
        });
    }

    console.log('\n   c) Other Document Keys:');
    const otherDocKeys = allKeys.filter(key =>
        key.includes('document') ||
        key.includes('Document') ||
        key.includes('draft') ||
        key.includes('template')
    ).filter(key => !key.startsWith('employeeDocuments_'));

    otherDocKeys.forEach(key => {
        const value = localStorage.getItem(key);
        try {
            const parsed = JSON.parse(value);
            if (Array.isArray(parsed)) {
                console.log(`      ${key}: ${parsed.length} items`);
            } else if (typeof parsed === 'object') {
                console.log(`      ${key}: object with ${Object.keys(parsed).length} keys`);
            }
        } catch {
            console.log(`      ${key}: ${value?.length || 0} characters`);
        }
    });
    console.log('\n');

    // 4. Test document flow
    console.log('4. TESTING DOCUMENT FLOW:');
    console.log('   Creating test document...');

    // eslint-disable-next-line no-unused-vars
    const testDoc = {
        id: Date.now(),
        category: 'test',
        templateName: 'Debug Test Document',
        content: 'This is a test document created by debug script',
        receivedAt: new Date().toISOString(),
        sentBy: 'Debug Script',
        type: 'company',
        status: 'received'
    };

    // Try to determine the correct employee ID
    const possibleIds = [
        currentUser.id,
        currentUser.employee_id,
        currentUser.user_id,
        currentUser.employee_code,
        'EMP001', // Common test ID
        'EMP002'
    ].filter(Boolean);

    console.log('   Possible employee IDs to test:', possibleIds);

    // Check which keys already exist
    console.log('\n   Checking which storage keys exist:');
    possibleIds.forEach(id => {
        const key = `employeeDocuments_${id}`;
        const exists = localStorage.getItem(key) !== null;
        if (exists) {
            const docs = JSON.parse(localStorage.getItem(key) || '[]');
            console.log(`      ✓ ${key} exists with ${docs.length} documents`);
        } else {
            console.log(`      ✗ ${key} does not exist`);
        }
    });

    console.log('\n========================================');
    console.log('   RECOMMENDATIONS:');
    console.log('========================================');

    // Provide recommendations based on findings
    if (!currentUser.id && !currentUser.employee_id) {
        console.log('⚠️  Current user has no ID fields set - this is likely the issue!');
        console.log('    Solution: Ensure user object has an id field when logging in');
    }

    if (empDocKeys.length === 0) {
        console.log('⚠️  No employee documents found in localStorage');
        console.log('    Solution: Check if documents are being saved with the correct key');
    } else {
        const userDocKey = empDocKeys.find(key =>
            key.includes(currentUser.id) ||
            key.includes(currentUser.employee_id) ||
            key.includes(currentUser.email)
        );

        if (!userDocKey) {
            console.log('⚠️  No documents found for current user');
            console.log('    Current user IDs:', {
                id: currentUser.id,
                employee_id: currentUser.employee_id,
                email: currentUser.email
            });
            console.log('    Available document keys:', empDocKeys);
            console.log('    Solution: Employee ID mismatch - documents are saved with different ID than user has');
        }
    }

    console.log('\n========================================\n');

    return {
        currentUser,
        documentKeys: empDocKeys,
        generatedDocuments: generatedDocs,
        recommendations: 'See console output above'
    };
};

// Make it available globally
window.debugDocuments = debugDocuments;

console.log('%c📋 Document Debug Script Loaded', 'color: green; font-weight: bold');
console.log('Run debugDocuments() in console to diagnose document issues');

export default debugDocuments;